import { DbRef, FeatureFlag, DraftWaypointStatus, WaypointStatusName, SPECIMEN_BAG_ID_PREFIX, FetchOrdersPageType } from '@caresend/types';
import { getStartOfLocalDate, nullishFilter, deduplicateArray, fetchWithRelationships, errorSuffix, isNullish, arrayToObj, unbindByContext } from '@caresend/utils';
import update from 'immutability-helper';
import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';
import { searchDenormalizedOrders } from '../../../database/algoliaConfig/methods.js';
import { getLocationNumericFilters } from '../../../database/algoliaConfig/model.js';
import { bindDraftWaypointDependencies } from '../../../components/specific/portal/store/portal/set/draftWaypoints.js';
import { ActionRequiredFilter, CurrentPastFilter, StatusFilter, StatusFilterType } from '../../../components/specific/VisitsPage/subComponents/OrdersVisitsFilters/model.js';
import { decryptData } from '../../../database/encryption/decrypt.js';
import { cancelOrderRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import { toastErrorAndReport, toastInfo } from '../../../functions/toast/toast.js';
import { hasMutation } from '../../helpers.js';
import { getStore } from '../../helpers/initStore.js';
import { isOfficeLab } from '../../modules/office/init.js';
import { removeOrderFromCurrentList, cancelledOrderToast } from '../../modules/orders/helpers.js';
import { useDraftWaypointsStore } from '../draftWaypoints/index.js';
import { generateProcedureRelationshipKeyObjectToNestLayer, draftWaypointRelationshipKeys, getUpdatedAlgoliaOrdersAPIKey, getPageParams, denormalizedOrderHasSortingDate } from './helpers.js';

const useOrdersStore = defineStore('orders', () => {
    const store = getStore();
    const algoliaKey = ref(null);
    const bookings = {};
    const denormalizedOrders = {};
    const draftWaypoints = {};
    const isEndOfList = ref(false);
    const isLoading = ref(true);
    const geofilters = ref(undefined);
    const nextPageCache = ref(undefined);
    const nextPageDenormalizedOrders = [];
    const offices = {};
    const orders = {};
    const placeGroups = {};
    const procedures = {};
    const shipments = {};
    const supplyTransfers = {};
    const users = {};
    const waypointActions = {};
    const waypoints = {};
    const state = reactive({
        algoliaKey,
        bookings,
        denormalizedOrders,
        draftWaypoints,
        isEndOfList,
        isLoading,
        geofilters,
        nextPageCache,
        nextPageDenormalizedOrders,
        offices,
        orders,
        placeGroups,
        procedures,
        shipments,
        supplyTransfers,
        users,
        waypointActions,
        waypoints,
    });
    const getters = {
        getBooking: (id) => !id ? undefined : state.bookings[id],
        getDenormalizedOrderByID: (id) => !id ? undefined : state.denormalizedOrders[id],
        getDenormalizedOrders: () => Object.values(state.denormalizedOrders),
        getDraftWaypoint: (id) => !id ? undefined : state.draftWaypoints[id],
        getOffice: (id) => !id ? undefined : state.offices[id],
        getOrder: (id) => !id ? undefined : state.orders[id],
        getOrderIDs: () => Object.keys(state.orders),
        getPlaceGroup: (id) => !id ? undefined : state.placeGroups[id],
        getProcedure: (id) => !id ? undefined : state.procedures[id],
        getProcedureInfo: (id) => !id ? undefined : state.procedures[id],
        getShipment: (id) => !id ? undefined : state.shipments[id],
        getSupplyTransfer: (id) => !id ? undefined : state.supplyTransfers[id],
        getUser: (id) => !id ? undefined : state.users[id],
        getWaypoint: (id) => !id ? undefined : state.waypoints[id],
        getBookingsOnOrder: (orderID) => {
            var _a;
            const order = getters.getOrder(orderID);
            if (!order)
                return [];
            const bookingIDs = Object.keys((_a = order.bookings) !== null && _a !== void 0 ? _a : {});
            const bookingsOnOrder = bookingIDs.map((bookingID) => getters.getBooking(bookingID)).filter(nullishFilter);
            return bookingsOnOrder;
        },
        getLabOrderIDsOnOrder: (orderID) => {
            const bookingsOnOrder = getters.getBookingsOnOrder(orderID);
            const labOrderIDs = bookingsOnOrder.map((booking) => booking.labOrderID).filter(nullishFilter);
            return deduplicateArray(labOrderIDs);
        },
        getPatientIDsFromRelatedProcedures: (orderID) => {
            const relatedProcedures = isOfficeLab()
                ? getters.getProceduresRelatedByPlaceGroup(orderID)
                : getters.getProceduresOnOrder(orderID);
            const maybeEncryptedPatientIDs = relatedProcedures.map((procedure) => {
                const booking = getters.getBooking(procedure.bookingID);
                return booking === null || booking === void 0 ? void 0 : booking.patientID;
            }).filter(nullishFilter);
            const ids = [];
            const deduplicatedPatientIDs = maybeEncryptedPatientIDs.filter((item) => {
                const idForDeduplication = typeof item === 'string' ? item : item.id;
                if (ids.includes(idForDeduplication))
                    return false;
                ids.push(idForDeduplication);
                return true;
            });
            return deduplicatedPatientIDs;
        },
        getProceduresOnOrder: (orderID) => {
            const bookingsOnOrder = getters.getBookingsOnOrder(orderID);
            const procedureIDs = bookingsOnOrder.map((booking) => { var _a; return Object.keys((_a = booking.procedures) !== null && _a !== void 0 ? _a : {}); }).flat();
            const proceduresOnOrder = procedureIDs.map((procedureID) => getters.getProcedure(procedureID)).filter(nullishFilter);
            return proceduresOnOrder;
        },
        getProceduresRelatedByPlaceGroup: (orderID) => {
            var _a, _b;
            if (!((_a = store.state.office) === null || _a === void 0 ? void 0 : _a.office))
                throw Error('Missing office');
            const officePlaceGroupID = (_b = store.state.office) === null || _b === void 0 ? void 0 : _b.office.placeGroupID;
            /**
             * There is no place group that is related to the office, so there wiill be
             * no related procedures here
             */
            if (!officePlaceGroupID)
                return [];
            const proceduresOnOrder = getters.getProceduresOnOrder(orderID);
            const filteredProcedures = proceduresOnOrder.filter((procedure) => { var _a; return (_a = procedure.dropOffPlaceGroups) === null || _a === void 0 ? void 0 : _a[officePlaceGroupID !== null && officePlaceGroupID !== void 0 ? officePlaceGroupID : '']; }).filter(nullishFilter);
            return filteredProcedures;
        },
        isThePrescriberOrInitiator: (orderID) => {
            var _a, _b;
            const officeID = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.id;
            if (!officeID)
                return false;
            const order = getters.getOrder(orderID);
            if (!order)
                return false;
            const officeIsInitiator = order.createdByOfficeID === officeID;
            const officeIsPrescriber = order.officeID === officeID;
            return officeIsInitiator || officeIsPrescriber;
        },
    };
    const writers = {
        [DbRef.BOOKINGS]: (inputBookings) => {
            state.bookings = update(state.bookings, {
                $merge: inputBookings,
            });
        },
        [DbRef.DRAFT_WAYPOINTS]: (inputDraftWaypoints) => {
            state.draftWaypoints = update(state.draftWaypoints, {
                $merge: inputDraftWaypoints,
            });
        },
        [DbRef.OFFICES]: (inputOffices) => {
            state.offices = update(state.offices, {
                $merge: inputOffices,
            });
        },
        [DbRef.ORDERS]: (inputOrders) => {
            state.orders = update(state.orders, {
                $merge: inputOrders,
            });
        },
        [DbRef.PLACE_GROUPS]: (inputPlaceGroups) => {
            state.placeGroups = update(state.placeGroups, {
                $merge: inputPlaceGroups,
            });
        },
        [DbRef.PROCEDURES]: (inputProcedures) => {
            state.procedures = update(state.procedures, {
                $merge: inputProcedures,
            });
        },
        [DbRef.SHIPMENTS]: (inputShipments) => {
            state.shipments = update(state.shipments, {
                $merge: inputShipments,
            });
        },
        [DbRef.SUPPLY_TRANSFERS]: (inputSupplyTransfers) => {
            state.supplyTransfers = update(state.supplyTransfers, {
                $merge: inputSupplyTransfers,
            });
        },
        [DbRef.USERS]: (inputUsers) => {
            state.users = update(state.users, {
                $merge: inputUsers,
            });
        },
        [DbRef.WAYPOINT_ACTIONS]: (inputWaypointActions) => {
            state.waypointActions = update(state.waypointActions, {
                $merge: inputWaypointActions,
            });
        },
        [DbRef.WAYPOINTS]: (inputWaypoints) => {
            state.waypoints = update(state.waypoints, {
                $merge: inputWaypoints,
            });
        },
    };
    const algoliaOfficeFilter = computed(() => {
        var _a, _b;
        let filter = '';
        const { officeID: officeIDFilter } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        if (officeIDFilter) {
            filter = ` AND officeIDs:${officeIDFilter}`;
        }
        return filter;
    });
    const algoliaSpecimenBagFilter = computed(() => {
        var _a, _b;
        const { specimenBagID } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        if (!specimenBagID) {
            return '';
        }
        let filtersSpecimenBag = '';
        if (!specimenBagID.includes(FeatureFlag.DISABLE_PATIENT_VISIT_SUPPLY_SCANNING)) {
            filtersSpecimenBag = `${SPECIMEN_BAG_ID_PREFIX}${specimenBagID}`;
        }
        else {
            filtersSpecimenBag = `${specimenBagID}`;
        }
        return ` AND specimenBagIDs:${filtersSpecimenBag}`;
    });
    const algoliaPrescriberFilter = computed(() => {
        var _a, _b;
        const { prescriberID } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        return prescriberID ? ` AND prescriber.userID:${prescriberID}` : '';
    });
    const algoliaPatientFilter = computed(async () => {
        var _a, _b, _c, _d;
        let filtersPatient = '';
        const { patientID } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        if (!patientID) {
            return filtersPatient;
        }
        try {
            const encryptedSecretID = (_d = (_c = store.state.users) === null || _c === void 0 ? void 0 : _c.users[patientID]) === null || _d === void 0 ? void 0 : _d.secretID;
            if (!encryptedSecretID) {
                return ` AND patientSecretIDs:${patientID}`;
            }
            const decryptedPatientSecretID = await decryptData(encryptedSecretID);
            filtersPatient = ` AND patientSecretIDs:${decryptedPatientSecretID}`;
        }
        catch (error) {
            toastErrorAndReport(error);
        }
        return filtersPatient;
    });
    const algoliaActionRequiredFilter = computed(() => {
        var _a, _b;
        const { actionRequiredFilter } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        if (actionRequiredFilter === ActionRequiredFilter.ALL)
            return '';
        const actionRequired = actionRequiredFilter === ActionRequiredFilter.ACTION_REQUIRED;
        return ` AND actionRequired:${actionRequired}`;
    });
    const algoliaCreationFilter = computed(() => ' creationTimestamp > 1688590800000');
    const algoliaDisplayCurrentPastFilter = computed(() => {
        var _a, _b;
        const { actionRequiredFilter } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        return actionRequiredFilter === ActionRequiredFilter.NO_ACTION_REQUIRED;
    });
    const algoliaDisplayPatientFilter = computed(() => !isOfficeLab());
    const algoliaDisplayPrescriberFilter = computed(() => !isOfficeLab());
    const algoliaPastCurrentFilter = computed(() => {
        var _a, _b;
        const { currentPastFilter, actionRequiredFilter } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        const startOfDay = getStartOfLocalDate(Date.now());
        if (actionRequiredFilter === ActionRequiredFilter.ACTION_REQUIRED)
            return '';
        switch (currentPastFilter) {
            case CurrentPastFilter.CURRENT:
                return ` AND sortingDate >= ${startOfDay} AND isCanceled=0`;
            case CurrentPastFilter.PAST:
                return ` AND (sortingDate < ${startOfDay} OR isCanceled=1)`;
            default:
                return '';
        }
    });
    const algoliaLabFilter = computed(() => {
        var _a;
        const officeID = (_a = store.state.office.office) === null || _a === void 0 ? void 0 : _a.id;
        return ` AND officeIDs:${officeID}`;
    });
    /**
     * This is currently unused because we are not searching real waypoints yet
     */
    const algoliaWaypointStatusFilter = computed(() => {
        var _a, _b;
        const { statusFilters } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        const status = 'waypointStatuses.status';
        const filters = [];
        if (statusFilters.includes(StatusFilter.CANCELED)) {
            filters.push(`draftWaypointStatuses.status:${DraftWaypointStatus.CANCELED}`);
        }
        if (statusFilters.includes(StatusFilter.COMPLETE)) {
            filters.push(`${status}:${WaypointStatusName.COMPLETE}`);
        }
        return filters.join(' OR ');
    });
    const algoliaDraftWaypointStatusFilter = computed(() => {
        var _a, _b;
        const { statusFilters } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        const status = 'draftWaypointStatuses.status';
        const filters = [];
        if (statusFilters.includes(StatusFilter.PENDING_SCHEDULING)) {
            filters.push(`${status}:${DraftWaypointStatus.FINALIZED}`);
            filters.push(`${status}:${DraftWaypointStatus.PENDING}`);
        }
        if (statusFilters.includes(StatusFilter.SCHEDULED)) {
            filters.push(`waypointStatuses.status:${WaypointStatusName.CREATED}`);
        }
        if (statusFilters.includes(StatusFilter.IN_ITINERARY)) {
            filters.push(`${status}:${DraftWaypointStatus.IN_ITINERARY}`);
        }
        if (statusFilters.includes(StatusFilter.COMPLETE)) {
            filters.push(`waypointStatuses.status:${WaypointStatusName.COMPLETE}`);
        }
        if (statusFilters.includes(StatusFilter.CANCELED)) {
            filters.push(`${status}:${DraftWaypointStatus.CANCELED}`);
            filters.push(`${status}:${DraftWaypointStatus.REJECTED}`);
        }
        return filters.join(' OR ');
    });
    const algoliaStatusFilter = computed(() => {
        var _a, _b, _c, _d;
        const { statusFilters } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        const { statusFilterType } = (_d = (_c = store.state.office) === null || _c === void 0 ? void 0 : _c.algolia.orders) !== null && _d !== void 0 ? _d : {};
        if (!statusFilters || statusFilters.length === 0)
            return '';
        let filters = ' AND (';
        if (statusFilterType !== StatusFilterType.DRAFT) {
            filters += algoliaWaypointStatusFilter.value;
        }
        if (statusFilterType === StatusFilterType.ALL) {
            filters += ' OR ';
        }
        if (statusFilterType !== StatusFilterType.WAYPOINT) {
            filters += algoliaDraftWaypointStatusFilter.value;
        }
        filters += ')';
        return filters;
    });
    const algoliaGeoFilter = computed(() => {
        if (geofilters.value) {
            const numericFilters = getLocationNumericFilters('geolocations', geofilters.value);
            return ` AND ${numericFilters.join(' AND ')}`;
        }
        return '';
    });
    const algoliaLabOrderFilter = computed(() => {
        var _a, _b;
        const { labOrderID } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        return (labOrderID === null || labOrderID === void 0 ? void 0 : labOrderID.length) ? ` AND labOrderIDs:${labOrderID}` : '';
    });
    const fetchFilters = async (payload) => {
        let filters = algoliaCreationFilter.value;
        if (payload.pageType === FetchOrdersPageType.VISITS) {
            filters += algoliaActionRequiredFilter.value + algoliaPastCurrentFilter.value;
        }
        else if (payload.pageType === FetchOrdersPageType.ITINERARY) {
            filters += algoliaStatusFilter.value;
        }
        switch (payload.pageType) {
            case FetchOrdersPageType.VISITS:
                filters += algoliaLabFilter.value;
                break;
            case FetchOrdersPageType.ITINERARY:
                filters += algoliaGeoFilter.value;
                break;
            case FetchOrdersPageType.ORDERS:
                filters += algoliaSpecimenBagFilter.value;
                break;
        }
        filters
            += await algoliaPatientFilter.value
                + algoliaPrescriberFilter.value
                + algoliaOfficeFilter.value
                + algoliaLabOrderFilter.value;
        return filters;
    };
    const executeLocalLabOrderFilter = (order) => {
        var _a, _b;
        const { labOrderID } = (_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.algolia.orders) !== null && _b !== void 0 ? _b : {};
        if (!labOrderID)
            return true;
        const labOrderIDs = getters.getLabOrderIDsOnOrder(order.id);
        return labOrderIDs.includes(labOrderID);
    };
    /**
     * Run the same logic as algolia filters on orders defined in office
     * localOrderIDs.
     *
     * Currently only supports lab order ID filtering.
     */
    const getShouldFilterOutLocalOrder = (order) => {
        const filterFunctions = [
            executeLocalLabOrderFilter,
        ];
        const someFilterReturnedFalse = filterFunctions.some((filter) => !filter(order));
        return someFilterReturnedFalse;
    };
    const bindOrder = async (orderID) => {
        try {
            await fetchWithRelationships(DbRef.ORDERS, orderID, {
                bookings: {
                    procedures: generateProcedureRelationshipKeyObjectToNestLayer(20, {
                        dropOffPlaceGroups: true,
                    }),
                    patientID: true,
                },
                draftWaypoints: draftWaypointRelationshipKeys,
                schedulingUserID: true,
                prescriberID: true,
                officeID: true,
                createdBy: true,
            }, { batchingContextID: 'orders', bind: true, writers });
        }
        catch (error) {
            console.error(error);
        }
    };
    const cancelOrder = async ({ orderID, reason, detailedReason, supplyCondition, userType, }) => {
        var _a;
        try {
            const denormalizedOrder = state.denormalizedOrders[orderID];
            if (!denormalizedOrder)
                throw Error('Order not found.');
            if (denormalizedOrder.isCanceled) {
                toastInfo('Order already cancelled.');
                return;
            }
            const officeID = (_a = store.state.office.office) === null || _a === void 0 ? void 0 : _a.id;
            if (!officeID)
                throw Error('Couldn’t find office information');
            await cancelOrderRequest({
                orderID,
                reason,
                detailedReason,
                supplyCondition,
                userType,
            });
            removeOrderFromCurrentList(orderID);
            cancelledOrderToast();
        }
        catch (error) {
            toastErrorAndReport(`There was a problem cancelling your order. ${errorSuffix}`);
        }
    };
    const clearOrdersAndWaypoints = () => {
        state.denormalizedOrders = {};
        state.draftWaypoints = {};
    };
    /** TODO: Possibly mark as @deprecated */
    const fetchDraftWaypoints = async () => {
        const draftWaypointIDs = Object.values(state.denormalizedOrders).map((order) => Object.values(order.draftWaypoints).map((draftWaypoint) => draftWaypoint.id).flat()).flat();
        Promise.all(draftWaypointIDs.map(async (draftWaypointID) => {
            if (state.draftWaypoints[draftWaypointID])
                return;
            await bindDraftWaypointDependencies({ id: draftWaypointID });
        }));
        const dwps = draftWaypointIDs.map((draftWaypointID) => store.getters['waypoint/getDraftWaypointByID'](draftWaypointID)).filter(nullishFilter);
        return dwps.forEach((draftWaypoint) => {
            state.draftWaypoints[draftWaypoint.id] = draftWaypoint;
        });
    };
    const fetchOrdersForPagination = async (payload) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        try {
            const rootState = getStore().state;
            state.isLoading = true;
            state.algoliaKey = await getUpdatedAlgoliaOrdersAPIKey(state.algoliaKey, false, (_a = rootState.office.office) === null || _a === void 0 ? void 0 : _a.id);
            state.geofilters = payload.geofilters;
            const filters = await fetchFilters(payload);
            const nextPageCacheValid = !isNullish(state.nextPageCache)
                && ((_b = state.nextPageCache) === null || _b === void 0 ? void 0 : _b.filters) === filters
                && state.nextPageCache.page === payload.page;
            let pageCache;
            if (nextPageCacheValid) {
                pageCache = state.nextPageCache;
            }
            const apiKey = (_d = (_c = state.algoliaKey) === null || _c === void 0 ? void 0 : _c.denormalizedOrdersKey) !== null && _d !== void 0 ? _d : '';
            const pageParams = getPageParams(payload.page, filters);
            const denOrders = (_e = pageCache === null || pageCache === void 0 ? void 0 : pageCache.results) !== null && _e !== void 0 ? _e : (await searchDenormalizedOrders(apiKey, '', pageParams)).hits;
            const denOrdersByID = arrayToObj(denOrders, 'id');
            state.denormalizedOrders = {
                ...state.denormalizedOrders,
                ...denOrdersByID,
            };
            await useDraftWaypointsStore()
                .storeDecryptedDenormalizedDraftWaypoints(denOrdersByID);
            const fullOrderIDSet = deduplicateArray(((_g = (_f = rootState.office) === null || _f === void 0 ? void 0 : _f.orderIDs) !== null && _g !== void 0 ? _g : []).concat(denOrders.map((order) => order.id)));
            const mappedOrders = fullOrderIDSet
                .map((id) => state.denormalizedOrders[id])
                .filter(nullishFilter)
                .filter(denormalizedOrderHasSortingDate)
                .sort((a, b) => b.sortingDate - a.sortingDate);
            if (hasMutation(store, 'office/SET_OFFICE_ORDERS')) {
                store.commit('office/SET_OFFICE_ORDERS', mappedOrders.map((order) => order.id));
            }
            if (!isNullish(payload.page)) {
                const nextDenormalizedOrders = await searchDenormalizedOrders((_j = (_h = state.algoliaKey) === null || _h === void 0 ? void 0 : _h.denormalizedOrdersKey) !== null && _j !== void 0 ? _j : '', '', getPageParams(payload.page + 1, filters));
                const endOfList = nextDenormalizedOrders.hits.length === 0;
                state.isEndOfList = endOfList;
                if (!endOfList) {
                    const nextPageOrdersCache = {
                        page: payload.page + 1,
                        results: nextDenormalizedOrders.hits,
                        filters,
                    };
                    state.nextPageCache = nextPageOrdersCache;
                }
            }
        }
        catch (error) {
            toastErrorAndReport(`There was a problem loading your orders. ${errorSuffix}`, error);
        }
        finally {
            state.isLoading = false;
        }
    };
    const unbindOrders = () => {
        unbindByContext('orders');
    };
    return {
        algoliaActionRequiredFilter,
        algoliaCreationFilter,
        algoliaDisplayCurrentPastFilter,
        algoliaDisplayPatientFilter,
        algoliaDisplayPrescriberFilter,
        algoliaDraftWaypointStatusFilter,
        algoliaGeoFilter,
        algoliaLabFilter,
        algoliaOfficeFilter,
        algoliaPastCurrentFilter,
        algoliaPatientFilter,
        algoliaPrescriberFilter,
        algoliaSpecimenBagFilter,
        algoliaStatusFilter,
        algoliaWaypointStatusFilter,
        bindOrder,
        cancelOrder,
        clearOrdersAndWaypoints,
        fetchDraftWaypoints,
        fetchOrdersForPagination,
        geofilters,
        getShouldFilterOutLocalOrder,
        getters,
        isEndOfList,
        isLoading,
        unbindOrders,
    };
});

export { useOrdersStore };
